import { Modal } from "@hakistudio/hakiui";
import { NOTE_TEMPLATES, type NoteColor, type NoteTemplate } from "@sebas/core";
import {
  CalendarDays,
  ChefHat,
  FileText,
  Refrigerator,
  ShoppingCart,
  Users,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * `core` names its icons rather than importing them, so the registry stays
 * framework-agnostic. This is where a name becomes a component.
 */
const ICONS: Record<string, LucideIcon> = {
  FileText,
  ChefHat,
  Refrigerator,
  CalendarDays,
  ShoppingCart,
  Users,
};

const TILE_BG: Record<NoteColor, string> = {
  sand: "bg-tile-sand",
  coral: "bg-tile-coral",
  amber: "bg-tile-amber",
  mint: "bg-tile-mint",
  sky: "bg-tile-sky",
  lilac: "bg-tile-lilac",
};

export function TemplateIcon({ icon, className }: { icon: string; className?: string }) {
  const Icon = ICONS[icon] ?? FileText;
  return <Icon className={className} aria-hidden />;
}

export function TemplatePicker({
  onPick,
  onClose,
  busy = false,
}: {
  onPick: (template: NoteTemplate) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  return (
    <Modal isOpen onClose={onClose} title="Start a note">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {NOTE_TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            disabled={busy}
            onClick={() => onPick(t)}
            className={cn(
              "flex items-start gap-3 rounded-card border border-line bg-raised p-4 text-left",
              "transition hover:-translate-y-0.5 hover:border-ink-faint disabled:opacity-60",
            )}
          >
            <span
              className={cn(
                "grid h-10 w-10 shrink-0 place-items-center rounded-full text-tile-ink",
                TILE_BG[t.color],
              )}
            >
              <TemplateIcon icon={t.icon} className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink">{t.label}</span>
              <span className="mt-0.5 block text-xs text-ink-muted">{t.description}</span>
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
